import { useSyncExternalStore } from 'react';

export const TABS = ['setup', 'players', 'rounds', 'standings', 'crosstable', 'log'] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABELS: Record<Tab, string> = {
  setup: 'Setup',
  players: 'Players',
  rounds: 'Rounds',
  standings: 'Standings',
  crosstable: 'Crosstable',
  log: 'Log',
};

export type Route =
  | { readonly name: 'list' }
  | { readonly name: 'new' }
  | { readonly name: 'tournament'; readonly id: string; readonly tab: Tab };

/**
 * Hash routes, so the app works from any folder and offline without server
 * rewrites: "#/", "#/new", "#/t/<id>/<tab>".
 */
export function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
  if (parts[0] === 'new') return { name: 'new' };
  if (parts[0] === 't' && parts[1] !== undefined) {
    const tab = (TABS as readonly string[]).includes(parts[2] ?? '')
      ? (parts[2] as Tab)
      : 'players';
    return { name: 'tournament', id: parts[1], tab };
  }
  return { name: 'list' };
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case 'list':
      return '#/';
    case 'new':
      return '#/new';
    case 'tournament':
      return `#/t/${encodeURIComponent(route.id)}/${route.tab}`;
  }
}

export function navigate(route: Route): void {
  window.location.hash = hrefFor(route);
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('hashchange', onChange);
  return () => window.removeEventListener('hashchange', onChange);
}

export function useHash(): string {
  return useSyncExternalStore(
    subscribe,
    () => window.location.hash,
    () => '',
  );
}
